import { CommonModule } from '@angular/common';
import { Component, ElementRef, NgZone, OnInit, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { VW_ERROR_CODES } from 'vwappservices';
import { VioTablesV2Module, VT2_TABLE_CONFIG } from 'vio-tables-v2';
import { MachinesApiService } from '../../services/machines-api.service';
import { MachineTypesApiService } from '../../services/machine-types-api.service';
import { notifyResult, reportApiError } from '../../shared/api-result.helper';
import { AppendToBodyDirective } from '../../shared/append-to-body.directive';
import { BilingualNameFieldComponent } from '../../shared/bilingual-name-field.component';
import { ConfirmService } from '../../shared/confirm.service';
import { BASE_VIO_TABLE_V2_CONFIG } from '../../shared/vio-table-v2.config';
import { openModalById } from '../../shared/bootstrap-modal.helper';

interface MachineRow {
  _id: string;
  name: string;
  nameMr: string;
  code: string;
  location: string;
  notes: string;
  machineTypeId: string | null;
  active: boolean;
}

interface MachineTypeOption {
  _id: string;
  name: string;
}

@Component({
  selector: 'app-machines',
  standalone: true,
  imports: [CommonModule, FormsModule, AppendToBodyDirective, BilingualNameFieldComponent, VioTablesV2Module],
  templateUrl: './machines.component.html',
  styleUrl: './machines.component.scss',
})
export class MachinesComponent implements OnInit {
  machines: MachineRow[] = [];
  tableData: any[] = [];
  machineTypes: MachineTypeOption[] = [];
  loading = true;

  tableConfig: VT2_TABLE_CONFIG = {
    ...BASE_VIO_TABLE_V2_CONFIG,
    tableTitle: 'Machines',
    excelFileName: 'machines',
    excludeFields: ['_id'],
    columnLabels: { name: 'Name', nameMr: 'Name (मराठी)', code: 'Code', machineType: 'Machine Type', location: 'Location', notes: 'Notes' },
    actionButtons: [
      {
        icon: '✏️',
        label: 'Edit',
        title: 'Edit machine',
        cssClass: 'btn btn-outline-primary btn-sm vio-table-icon-btn vio-icon-edit',
        onClick: (row: any) => {
          const machine = this.machines.find((m) => m._id === row._id);
          if (!machine) return;
          this.openEditModal(machine);
          openModalById('machineModal');
        },
      },
      {
        icon: '🗑️',
        label: 'Delete',
        title: 'Delete machine',
        // vio-icon-delete's ::before icon inherits `color` from .btn-outline-danger, so it renders
        // as a proper red Font Awesome trash icon in an outlined box — matching the same delete
        // buttons used elsewhere in the app (e.g. Settings' downtime-reasons list).
        cssClass: 'btn btn-outline-danger btn-sm vio-table-icon-btn vio-icon-delete',
        // deleteMachine() already shows the app's own styled confirm dialog (ConfirmService) —
        // not using requireConfirm here to avoid stacking a second, differently-styled prompt.
        onClick: (row: any) => {
          const machine = this.machines.find((m) => m._id === row._id);
          if (machine) this.deleteMachine(machine);
        },
      },
    ],
  };

  operation: 'Add' | 'Edit' = 'Add';
  editingId: string | null = null;
  machineForm = { name: '', nameMr: '', code: '', location: '', notes: '', machineTypeId: '' };

  @ViewChild('machineModalCloseBtn') machineModalCloseBtn!: ElementRef<HTMLButtonElement>;

  private toastr = inject(ToastrService);
  private confirmService = inject(ConfirmService);

  constructor(
    private machinesApi: MachinesApiService,
    private machineTypesApi: MachineTypesApiService,
    private ngZone: NgZone,
  ) {}

  ngOnInit() {
    this.load();
  }

  async load() {
    this.loading = true;
    const [machinesRes, machineTypesRes] = await Promise.all([this.machinesApi.list(), this.machineTypesApi.list()]);
    // Same zone-continuation issue documented in DashboardComponent/SettingsComponent's load() —
    // without this, `loading` flips to false internally but the view stays stuck.
    this.ngZone.run(() => {
      if (machinesRes.errorCode === VW_ERROR_CODES.SUCCESS) {
        this.machines = machinesRes.Data;
      } else {
        reportApiError(this.toastr, machinesRes);
      }
      if (machineTypesRes.errorCode === VW_ERROR_CODES.SUCCESS) {
        this.machineTypes = machineTypesRes.Data;
      } else {
        reportApiError(this.toastr, machineTypesRes);
      }
      this.tableData = this.machines.map((m) => ({
        _id: m._id,
        name: m.name,
        nameMr: m.nameMr || '—',
        code: m.code,
        machineType: this.machineTypeName(m.machineTypeId),
        location: m.location || '—',
        notes: m.notes || '—',
      }));
      this.loading = false;
    });
  }

  machineTypeName(machineTypeId: string | null): string {
    if (!machineTypeId) return '—';
    return this.machineTypes.find((mt) => mt._id === machineTypeId)?.name || '—';
  }

  openAddModal() {
    this.operation = 'Add';
    this.editingId = null;
    this.machineForm = { name: '', nameMr: '', code: '', location: '', notes: '', machineTypeId: '' };
  }

  openEditModal(machine: MachineRow) {
    this.operation = 'Edit';
    this.editingId = machine._id;
    this.machineForm = {
      name: machine.name,
      nameMr: machine.nameMr || '',
      code: machine.code,
      location: machine.location,
      notes: machine.notes,
      machineTypeId: machine.machineTypeId || '',
    };
  }

  async saveMachine() {
    if (!this.machineForm.name || !this.machineForm.code) return;
    const payload = {
      name: this.machineForm.name,
      nameMr: this.machineForm.nameMr,
      location: this.machineForm.location,
      notes: this.machineForm.notes,
      machineTypeId: this.machineForm.machineTypeId || null,
    };
    const res =
      this.operation === 'Add'
        ? await this.machinesApi.create({ ...payload, code: this.machineForm.code })
        : await this.machinesApi.update(this.editingId!, payload);
    if (!notifyResult(this.toastr, res, `${this.machineForm.name} ${this.operation === 'Add' ? 'added' : 'updated'}.`)) return;
    this.machineModalCloseBtn.nativeElement.click();
    await this.load();
  }

  async deleteMachine(machine: MachineRow) {
    const ok = await this.confirmService.confirm({
      message: `Delete machine "${machine.name}"? This cannot be undone.`,
      confirmText: 'Delete',
      danger: true,
    });
    if (!ok) return;
    const res = await this.machinesApi.delete(machine._id);
    if (!notifyResult(this.toastr, res, `${machine.name} deleted.`)) return;
    await this.load();
  }
}
